import { createContext, useState, useMemo, useContext, useEffect } from "react";
import { Flex, Button, Table, Checkbox } from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import StyledModal from "./style";

import { MODEL_COLUMN_PREFERENCES } from "../../constant";

import { ReactComponent as Reorder } from "../../image/icon_reorder.svg";

const RowContext = createContext({});
const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<Reorder />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const Row = props => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });
  const style = Object.assign(
    Object.assign(Object.assign({}, props.style), {
      transform: CSS.Translate.toString(transform),
      transition,
    }),
    isDragging ? { position: "relative", zIndex: 9999 } : {}
  );
  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const ModalAdjustColumns = ({
  columnPreferences,
  onCancel,
  onSuccess,
  open,
}) => {
  const [dataSource, setDataSource] = useState(
    columnPreferences ?? MODEL_COLUMN_PREFERENCES
  );

  useEffect(() => {
    if (!open) return;

    onSuccess(dataSource);
  }, [dataSource]);

  const onCheckboxChange = ({ isVisible, type }) => {
    setDataSource(lastState =>
      lastState.map(it => {
        if (it.type === type) {
          return {
            ...it,
            visible: isVisible,
          };
        } else {
          return it;
        }
      })
    );
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
      setDataSource(prevState => {
        const activeIndex = prevState.findIndex(
          record =>
            record.key ===
            (active === null || active === void 0 ? void 0 : active.id)
        );
        const overIndex = prevState.findIndex(
          record =>
            record.key === (over === null || over === void 0 ? void 0 : over.id)
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };

  const columns = [
    {
      render: allValues => (
        <Checkbox
          disabled={allValues.type === "name" || allValues.type === "weight"}
          checked={allValues.visible}
          onChange={event =>
            onCheckboxChange({
              isVisible: event.target.checked,
              type: allValues.type,
            })
          }
        >
          {allValues.label}
        </Checkbox>
      ),
    },
    { align: "right", key: "sort", render: () => <DragHandle />, width: 30 },
  ];

  return (
    <StyledModal
      closeIcon={false}
      footer={null}
      onCancel={onCancel}
      open={open}
      title={
        <Flex align="center" justify="space-between">
          <h4>Adjust Columns</h4>
          <Button onClick={onCancel} shape="round">
            Close
          </Button>
        </Flex>
      }
      width={402}
    >
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={
            Array.isArray(dataSource)
              ? dataSource.map(i => i.key)
              : MODEL_COLUMN_PREFERENCES
          }
          strategy={verticalListSortingStrategy}
        >
          <Table
            columns={columns}
            components={{ body: { row: Row } }}
            dataSource={dataSource}
            pagination={false}
            rowHoverable={false}
            showHeader={false}
            size="small"
            rowKey="key"
          />
        </SortableContext>
      </DndContext>
    </StyledModal>
  );
};

export default ModalAdjustColumns;
